import React, { useState, useEffect } from "react";
import { Button, Header, Icon, Modal, Radio, Table } from "semantic-ui-react";
import {
  CONFIG_TABLE_COLUMNS,
  FILE_STORAGE_API_ENDPOINT,
  ConfigStatus,
} from "common/constants";
import ErrorModalState from "components/ErrorModalState";
import InternalServerErrorState from "components/InternalServerErrorState";
import NotFoundState from "components/NotFoundState";
import { FormattedMessage } from "react-intl";
import { patch } from "utils/requests";
import { connect } from "react-redux";
import { apiToken } from "services/legit/token";
import { RootState } from "StoreTypes";
import EmptyState from "components/EmptyState";
import styles from "./styles.module.scss";
import messages from "./messages";

export interface TableProps {
  token: string | null;
  records: any[] | undefined;
  onRowClick?: (obj: any) => void;
  handleSubmit: () => void;
}

const ConfigTable = ({
  handleSubmit,
  records,
  onRowClick,
  token,
}: TableProps) => {
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);
  const [archive, setArchive] = useState(false);
  const [empty, setEmpty] = useState(false);
  const [archiveEmpty, setArchiveEmpty] = useState(true);
  const [activeEmpty, setActiveEmpty] = useState(true);
  const [internalServerError, setInternalServerError] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [headers] = useState(
    new Headers({
      "Content-Type": "application/json",
      Authorization: token as string,
    })
  );

  useEffect(() => {
    if (records?.length === 0) {
      checkApplicationStatus();
    } else {
      setActiveEmpty(true);
      setArchiveEmpty(true);
      checkStatus();
    }
  }, [
    empty,
    archiveEmpty,
    activeEmpty,
    archive,
    checkApplicationStatus,
    records,
  ]);

  function checkApplicationStatus() {
    setActiveEmpty(true);
    setArchiveEmpty(true);

    if (records?.length === undefined || records?.length > 0) {
      checkStatus();
    } else {
      setEmpty(true);
    }
  }

  function checkStatus() {
    // eslint-disable-next-line array-callback-return
    records?.map((app: any) => {
      switch (app.status) {
        case "ACTIVE":
          setActiveEmpty(false);
          setEmpty(false);
          break;
        case "ARCHIVED":
          setArchiveEmpty(false);
          setEmpty(false);
          break;
        default:
          setEmpty(false);
      }
    });
  }

  const renderEmptyState = () => {
    return (
      <Table.Row>
        <Table.Cell>
          <EmptyState />
        </Table.Cell>
      </Table.Row>
    );
  };

  function archiveConfig(e: any, app: any) {
    const initialUpdateConfig = {
      accepted_mime_types: app?.config.accepted_mime_types,
      pubsub_topic: app?.config.pubsub_topic,
      max_file_size: app?.config.max_file_size,
      read_permission: app?.config.read_permission,
      write_permission: app?.config.write_permission,
      bucket_name: app?.config.bucket_name,
    };
    let initialUpdateAppConfig = {
      id: app?.id,
      config: initialUpdateConfig,
      application_name: app?.application_name,
      updated_by: app?.updated_by,
      status: app?.status,
    };
    if (archive) {
      setActiveEmpty(false);
      initialUpdateAppConfig = {
        id: app?.id,
        config: initialUpdateConfig,
        application_name: app?.application_name,
        updated_by: app?.updated_by,
        status: ConfigStatus.Active,
      };
    } else {
      setArchiveEmpty(false);
      initialUpdateAppConfig = {
        id: app?.id,
        config: initialUpdateConfig,
        application_name: app?.application_name,
        updated_by: app?.updated_by,
        status: ConfigStatus.Archived,
      };
    }
    const request = JSON.stringify(initialUpdateAppConfig);
    const endpoint = `${FILE_STORAGE_API_ENDPOINT}/${app.id}`;
    patch(endpoint, request, headers, true)
      .then((res) => {
        handleSubmit();
      })
      .catch((error) => {
        if (error.response?.status === 500) {
          setInternalServerError(true);
          setError(true);
        } else if (error.response?.status === 404) {
          setNotFound(true);
          setError(true);
        } else {
          setError(true);
        }
      });
    e.stopPropagation();
  }

  const renderErrorState = () => {
    if (internalServerError) {
      return <InternalServerErrorState />;
    }
    if (notFound) {
      return <NotFoundState />;
    }
    return <ErrorModalState />;
  };

  function archiveAppConfig() {
    if (archive) {
      setArchive(false);
    } else {
      setArchive(true);
    }
  }

  const confirmFinalSubmission = (confirmed: boolean, e: any, app: any) => {
    if (confirmed) {
      archiveConfig(e, app);
      setOpenConfirmationModal(false);
    } else {
      setOpenConfirmationModal(false);
    }
  };

  const confirmArchive = (e: any) => {
    setOpenConfirmationModal(true);
    e.stopPropagation();
  };

  function getDate(t: any) {
    const date = new Date(t);

    return `${date.getDate()}/${
      date.getMonth() + 1
    }/${date.getFullYear()} ${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}`;
  }

  return (
    <div>
      <Radio
        type="radio"
        className={styles.viewArchive}
        toggle
        label={archive ? "View archived configs" : "View unarchived configs"}
        onClick={() => archiveAppConfig()}
      />
      {error ? (
        <>{renderErrorState()}</>
      ) : (
        <div className={styles.table}>
          <Table id="config-table" celled fixed>
            <Table.Header id="table-header">
              <Table.Row id="table-header-row">
                {CONFIG_TABLE_COLUMNS.map((col: any) => (
                  <Table.HeaderCell
                    className={styles.tableHeader}
                    key={`header-${col}`}
                  >
                    {col}
                  </Table.HeaderCell>
                ))}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {empty ? (
                <>{renderEmptyState()}</>
              ) : (
                <>
                  {!archive ? (
                    <>{activeEmpty && <>{renderEmptyState()}</>}</>
                  ) : (
                    <>{archiveEmpty && <>{renderEmptyState()}</>}</>
                  )}
                  {records?.map((app) => (
                    <Table.Row
                      id={`config-${app.id}`}
                      key={`config-${app.id}`}
                      onClick={() => {
                        if (onRowClick) {
                          onRowClick(app);
                        }
                      }}
                    >
                      {archive === (app.status === ConfigStatus.Archived) && (
                        <>
                          <Table.Cell>
                            <Button
                              className={styles.archive}
                              basic
                              id={`archive-${app.id}`}
                              color="orange"
                              icon="file archive outline"
                              onClick={(e) => confirmArchive(e)}
                              content={
                                app.status === "ACTIVE"
                                  ? "Archive"
                                  : "Unarchive"
                              }
                            />
                          </Table.Cell>
                          <Table.Cell>{`${app.application_name}`}</Table.Cell>
                          <Table.Cell>{`${app.config_version}`}</Table.Cell>
                          <Table.Cell>{`${app.config.pubsub_topic}`}</Table.Cell>
                          <Table.Cell>{`${app.config.bucket_name}`}</Table.Cell>
                          <Table.Cell>{`${app.config.accepted_mime_types}`}</Table.Cell>
                          <Table.Cell>{`${app.config.max_file_size}`}</Table.Cell>
                          <Table.Cell>{`${app.config.write_permission}`}</Table.Cell>
                          <Table.Cell>{`${app.config.read_permission}`}</Table.Cell>
                          <Table.Cell>{getDate(app.created_at)}</Table.Cell>
                          <Table.Cell>{getDate(app.updated_at)}</Table.Cell>
                          <Table.Cell>{`${app.updated_by}`}</Table.Cell>
                          <Table.Cell>{`${app.deleted}`}</Table.Cell>
                          <Table.Cell>{getDate(app.deleted_at)}</Table.Cell>
                          <Table.Cell>{`${app.status}`}</Table.Cell>
                        </>
                      )}
                    </Table.Row>
                  ))}
                </>
              )}
            </Table.Body>
          </Table>
          {records?.map((app) => (
            <div key={`archive-${app.id}`}>
              {archive === (app.status === ConfigStatus.Archived) && (
                <Modal
                  size="small"
                  basic
                  open={openConfirmationModal}
                  id="confirmation-modal"
                >
                  <Header as="h3" icon>
                    <Icon name="archive" />
                    <FormattedMessage {...messages.confirmChanges} />
                  </Header>
                  <Modal.Content>
                    <p>
                      <FormattedMessage {...messages.confirmChangesMessage} />
                      {" - "}
                      {`${app.application_name}`}
                    </p>
                  </Modal.Content>
                  <Modal.Actions>
                    <Button
                      id="cancel-submission"
                      basic
                      color="red"
                      inverted
                      onClick={(e) => confirmFinalSubmission(false, e, app)}
                    >
                      <Icon name="remove" />
                      <FormattedMessage {...messages.cancel} />
                    </Button>
                    <Button
                      id="confirm-submission"
                      color="green"
                      inverted
                      onClick={(e) => confirmFinalSubmission(true, e, app)}
                    >
                      <Icon name="checkmark" />
                      <FormattedMessage {...messages.submit} />
                    </Button>
                  </Modal.Actions>
                </Modal>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  token: apiToken(state),
});

export default connect(mapStateToProps)(ConfigTable);
